/* ---------------------------------------------------------------------------
 * TEMPORARY DIAGNOSTICS — remove once the iOS stuck-overlay bug is confirmed.
 * Enable by appending ?stldebug=1 to the URL. No-op otherwise.
 * ------------------------------------------------------------------------- */
const STL_DEBUG = typeof location !== 'undefined' && /[?&]stldebug=1/.test(location.search);

const stlLog = (...args) => {
  if (STL_DEBUG) console.log('[STL]', ...args);
};

const stlSnapshot = (tag) => {
  if (!STL_DEBUG) return;

  const popups = Array.from(document.querySelectorAll('.popup'));
  const rows = popups.map((popup, i) => {
    const style = getComputedStyle(popup);
    const rect = popup.getBoundingClientRect();
    const prev = popup.previousElementSibling;
    const container = popup.querySelector('.popup-container');

    return {
      i,
      id: popup.id || '(no id)',
      cls: popup.className,
      // Did the attribute mutation actually land in the DOM?
      prevIsDetails: prev ? prev.tagName.toLowerCase() : '(none)',
      prevHasOpen: prev ? prev.hasAttribute('open') : null,
      prevOpenValue: prev ? prev.getAttribute('open') : null,
      popupOpenAttr: popup.getAttribute('open'),
      // Did the CSS re-evaluate?
      visibility: style.visibility,
      display: style.display,
      bg: style.backgroundColor,
      zIndex: style.zIndex,
      rect: `${Math.round(rect.width)}x${Math.round(rect.height)}`,
      containerOpacity: container ? getComputedStyle(container).opacity : '(none)',
      containerTransform: container ? getComputedStyle(container).transform : '(none)',
    };
  });

  const stuck = rows.filter((r) => r.visibility === 'visible');

  console.log(
    `[STL] ---- ${tag} ---- popups=${rows.length} computedVisible=${stuck.length}`
  );
  console.table(rows);

  if (stuck.length) {
    stuck.forEach((r) => {
      if (r.prevHasOpen === false) {
        // [open] gone but CSS still says visible => style invalidation failure
        console.warn(
          `[STL] HYPOTHESIS B (stale style): popup #${r.i} visible while its <details> has no [open]`
        );
      } else if (r.prevHasOpen === true) {
        // [open] still there => close() never ran, or ran against another element
        console.warn(
          `[STL] HYPOTHESIS A/C (close never applied): popup #${r.i} still has [open] on its <details>`
        );
      }
    });
  }
};

if (STL_DEBUG) {
  console.log('[STL] diagnostics ON');
  console.log('[STL] UA:', navigator.userAgent);
  console.log(
    '[STL] dvh supported:',
    typeof CSS !== 'undefined' && CSS.supports ? CSS.supports('height', '100dvh') : 'unknown'
  );
  window.stlSnapshot = stlSnapshot;
  document.addEventListener('DOMContentLoaded', () => stlSnapshot('DOMContentLoaded'));
}

class DetailsModal extends HTMLElement {
  constructor() {
    super();
    this.detailsContainer = this.querySelector('details');
    this.summaryToggle = this.querySelector('summary');
    // Null for the header search modal (it uses .search-modal, not .popup),
    // so every popup branch below is a no-op there.
    this.popup = this.querySelector('.popup');

    stlLog('constructed', {
      hasDetails: !!this.detailsContainer,
      detailsId: this.detailsContainer?.id ?? null,
      hasSummary: !!this.summaryToggle,
      hasCloseBtn: !!this.querySelector('button[type="button"].modal__close-button'),
      hasStlPopup: !!this.querySelector('.shop-the-look__popup'),
      childCount: this.children.length,
    });

    this.detailsContainer?.addEventListener('keyup', (event) => event.code.toUpperCase() === 'ESCAPE' && this.close());
    this.summaryToggle?.addEventListener('click', this.onSummaryClick.bind(this));
    this.querySelector('button[type="button"].modal__close-button')?.addEventListener('click', this.close.bind(this));

    this.summaryToggle?.setAttribute('role', 'button');

    document.addEventListener("click", event => {
      if (this.isOpen() && !!event.target.closest('.modal-overlay')) {
        console.log("closing...")
        this.close()
      }
    })

    this.querySelector(".shop-the-look__popup")?.addEventListener("click", event => {
      if(!!!event.target.closest(".popup-container")) {
        console.log("closing...")
        this.close()
      }
    })

    // HYPOTHESIS C probe: does iOS deliver a `click` to the non-interactive
    // backdrop <div> at all? touchend fires regardless; click may not.
    if (STL_DEBUG) {
      const backdrop = this.querySelector('.shop-the-look__popup');
      ['touchend', 'pointerup', 'click'].forEach((type) => {
        backdrop?.addEventListener(type, (event) => {
          stlLog(`backdrop ${type}`, {
            target: event.target.className || event.target.tagName,
            insideContainer: !!event.target.closest('.popup-container'),
          });
        });
      });
      this.querySelector('button[type="button"].modal__close-button')?.addEventListener(
        'click',
        () => stlLog('close button click fired')
      );
    }

    window.addEventListener('keydown', event => {
      if (this.isOpen() && event.key === 'Escape') {
        this.close();
      }
    })
  }

  isOpen() {
    return !!this.detailsContainer?.hasAttribute('open');
  }

  onSummaryClick(event) {
    event.preventDefault();
    const details = event.target.closest('details') || this.detailsContainer;
    details?.hasAttribute('open') ? this.close() : this.open(event);
  }

  /**
   * Restores the popup to its laid-out-but-hidden state and flushes styles, so
   * the .popup-container entry transition still has a starting frame after
   * hidePopup() took the element out of flow with `display: none`.
   */
  preparePopup() {
    if (!this.popup) return;
    this.popup.style.removeProperty('display');
    this.popup.style.removeProperty('visibility');
    void this.popup.offsetHeight; // force reflow to commit the closed styles
  }

  /**
   * Hides the popup without relying on the `details[open] + .popup` sibling
   * selector being re-evaluated. Inline styles outrank any stylesheet rule, and
   * `display: none` also drops the fixed-position compositing layer that iOS
   * Safari can otherwise leave painted after `visibility` flips.
   */
  hidePopup() {
    if (!this.popup) return;
    this.popup.classList.remove('popup--open');
    this.popup.style.visibility = 'hidden';
    this.popup.style.display = 'none';
  }

  onBodyClick(event) {
    // if (!this.contains(event.target) || event.target.classList.contains('modal-overlay')) this.close(false);
  }

  open(event) {
    this.onBodyClickEvent = this.onBodyClickEvent || this.onBodyClick.bind(this);
    const target = event.target.closest('details');

    // HYPOTHESIS A probe: open() and close() must act on the SAME element.
    stlLog('open()', {
      targetId: target?.id ?? null,
      cachedId: this.detailsContainer?.id ?? null,
      sameElement: target === this.detailsContainer,
    });

    // Order matters: lay the popup out while still hidden and flush styles
    // BEFORE flipping it visible, otherwise the entry transition has no
    // starting frame to animate from.
    this.preparePopup();

    (target || this.detailsContainer)?.setAttribute('open', true);
    this.popup?.classList.add('popup--open');

    document.body.addEventListener('click', this.onBodyClickEvent);
    document.body.classList.add('overflow-hidden');

    if (STL_DEBUG) requestAnimationFrame(() => stlSnapshot('after open()'));
  }

  close(focusToggle = true) {
    stlLog('close() entered', { cachedId: this.detailsContainer?.id ?? null });

    this.detailsContainer?.removeAttribute('open');

    // Hide the popup explicitly. This must not depend on the [open] removal
    // above having taken effect, so it runs unconditionally and last-write-wins.
    this.hidePopup();

    stlLog('close() removed [open]', {
      stillOpen: !!this.detailsContainer?.hasAttribute('open'),
    });

    document.body.removeEventListener('click', this.onBodyClickEvent);
    document.body.classList.remove('overflow-hidden');

    if (STL_DEBUG) {
      // Sampled across frames: distinguishes a stale *style* (computed stays
      // "visible") from a stale *paint* (computed flips to "hidden" but the
      // grey stays on screen until something forces a repaint).
      stlSnapshot('close() sync');
      requestAnimationFrame(() => stlSnapshot('close() +1 frame'));
      setTimeout(() => stlSnapshot('close() +400ms'), 400);
      setTimeout(() => stlSnapshot('close() +1200ms'), 1200);
    }
  }
}

customElements.define('details-modal', DetailsModal);
